import { asyncStorage } from '@/core/storage';
import { type ColorSchemeName } from 'react-native';
import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';

type ThemeStoreState = {
    preference: ThemePreference;
    systemScheme: NonNullable<ColorSchemeName>;
    setPreference: (preference: ThemePreference) => void;
    setSystemScheme: (scheme: NonNullable<ColorSchemeName>) => void;
    toggle: () => void; // toggles between light and dark, keeps system if set explicitly later
};

export const THEME_PREFERENCE_STORAGE_KEY = 'theme.preference';

export const useThemeStore = create<ThemeStoreState>((set, get) => ({
    // NOTE: Theme switching is currently disabled
    // We are not using themes in our app at this time, so the preference is
    // always set to 'light'. System theme detection is disabled.
    // In the future, when we implement theme support, we will enable this functionality.
    preference: 'light', // Always 'light' - themes are disabled
    systemScheme: 'light', // Fixed to 'light' - system theme detection is disabled
    setPreference: (preference) => {
        set({ preference });
        void asyncStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
    },
    setSystemScheme: (scheme) => set({ systemScheme: scheme }),
    toggle: () => {
        const { preference, setPreference, systemScheme } = get();
        if (preference === 'system') {
            setPreference(systemScheme === 'dark' ? 'light' : 'dark');
        } else {
            setPreference(preference === 'dark' ? 'light' : 'dark');
        }
    },
}));

export const getEffectiveScheme = (preference: ThemePreference, systemScheme: NonNullable<ColorSchemeName>): 'light' | 'dark' => {
    return preference === 'system' ? systemScheme : preference;
};


